import { audiusApiClient } from './AudiusApiClient';
import { AUDIUS_MOOD_PRESETS, DEFAULT_AUDIUS_MOOD } from './audiusMoodPresets';
import { AudiusMoodId, AudiusPlayerState, AudiusTrackSummary } from './audiusTypes';
import { AudioSettings, loadAudioSettings, onAudioSettingsChanged, saveAudioSettings } from '@/utils/audioSettings';

type PlayerSubscriber = (state: AudiusPlayerState) => void;
const AUDIO_MOOD_KEY = 'clair_holdem_audius_mood_v1';

const shuffle = <T,>(items: T[]): T[] => {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

const buildInitialState = (): AudiusPlayerState => {
  const audioSettings = loadAudioSettings();
  const selectedMood = loadSelectedMood();
  return {
    queue: [],
    currentTrack: null,
    currentIndex: -1,
    isPlaying: false,
    isLoading: false,
    isPanelOpen: false,
    hasActivated: false,
    hasInitializedQueue: false,
    currentTime: 0,
    duration: 0,
    selectedMood,
    searchQuery: '',
    searchResults: [],
    error: null,
    volume: audioSettings.musicVolume,
  };
};

const loadSelectedMood = (): AudiusMoodId => {
  try {
    const stored = localStorage.getItem(AUDIO_MOOD_KEY);
    if (!stored) return DEFAULT_AUDIUS_MOOD as AudiusMoodId;
    const match = AUDIUS_MOOD_PRESETS.find((preset) => preset.id === stored);
    return match?.id || (DEFAULT_AUDIUS_MOOD as AudiusMoodId);
  } catch {
    return DEFAULT_AUDIUS_MOOD as AudiusMoodId;
  }
};

const saveSelectedMood = (moodId: AudiusMoodId): void => {
  try {
    localStorage.setItem(AUDIO_MOOD_KEY, moodId);
  } catch {
    // ignore storage issues
  }
};

class AudiusPlayerService {
  private state: AudiusPlayerState = buildInitialState();
  private subscribers = new Set<PlayerSubscriber>();
  private readonly audio = new Audio();
  private unsubscribeAudioSettings: (() => void) | null = null;
  private searchAbortController: AbortController | null = null;
  private autoResumeWhenVolumeRestored = false;
  private manualPause = false;
  private playbackToken = 0;

  private isBenignAbortError(error: unknown): boolean {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return true;
    }

    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      if (error.name === 'AbortError') {
        return true;
      }
      return (
        message.includes('fetching process for the media resource was aborted') ||
        message.includes('the play() request was interrupted') ||
        message.includes('aborterror')
      );
    }

    return false;
  }

  constructor() {
    this.audio.preload = 'none';
    this.audio.crossOrigin = 'anonymous';
    this.bindAudioEvents();
    this.unsubscribeAudioSettings = onAudioSettingsChanged((settings) => {
      this.handleExternalAudioSettings(settings);
    });
    this.applyVolume(this.state.volume);
  }

  subscribe(listener: PlayerSubscriber): () => void {
    this.subscribers.add(listener);
    listener(this.getState());
    return () => {
      this.subscribers.delete(listener);
    };
  }

  getState(): AudiusPlayerState {
    return {
      ...this.state,
      queue: [...this.state.queue],
      searchResults: [...this.state.searchResults],
    };
  }

  setPanelOpen(isOpen: boolean): void {
    this.patchState({ isPanelOpen: isOpen });
  }

  setSearchQuery(query: string): void {
    this.patchState({ searchQuery: query });
  }

  getMoodPresets() {
    return AUDIUS_MOOD_PRESETS;
  }

  async setMood(moodId: AudiusMoodId): Promise<void> {
    if (this.state.selectedMood === moodId && this.state.queue.length > 0) {
      return;
    }

    saveSelectedMood(moodId);
    this.patchState({
      selectedMood: moodId,
      isLoading: true,
      error: null,
      currentTime: 0,
      duration: 0,
      queue: [],
      currentTrack: null,
      currentIndex: -1,
    });

    try {
      const queue = await this.fetchFreshQueue([], moodId);
      const currentTrack = queue[0] || null;
      this.patchState({
        hasInitializedQueue: true,
        isLoading: false,
        queue,
        currentTrack,
        currentIndex: currentTrack ? 0 : -1,
        duration: currentTrack?.durationSec || 0,
        error: currentTrack ? null : 'Nie udało się zbudować playlisty dla wybranego mooda.',
      });

      if (currentTrack) {
        this.setAudioSource(currentTrack);
        if (this.state.hasActivated && this.state.volume > 0) {
          await this.play();
        }
      }
    } catch (error) {
      this.patchState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Nie udało się zmienić playlisty mood.',
      });
    }
  }

  async activate(): Promise<void> {
    const wasActivated = this.state.hasActivated;
    if (!wasActivated) {
      this.patchState({
        hasActivated: true,
        error: null,
      });
    }

    if (!this.state.hasInitializedQueue || !this.state.currentTrack) {
      await this.ensureQueueInitialized();
    }

    if ((!wasActivated || !this.state.currentTrack) && this.state.volume > 0 && this.state.currentTrack) {
      await this.play();
    }
  }

  async ensureQueueInitialized(): Promise<void> {
    if (this.state.hasInitializedQueue) {
      return;
    }

    this.patchState({ isLoading: true, error: null });

    try {
      let queue = await this.fetchFreshQueue([], this.state.selectedMood);
      if (queue.length === 0) {
        const bootstrap = await audiusApiClient.getBootstrap();
        queue = bootstrap.queue;
      }

      const currentTrack = queue[0] || null;
      this.patchState({
        hasInitializedQueue: true,
        isLoading: false,
        queue,
        currentTrack,
        currentIndex: currentTrack ? 0 : -1,
        duration: currentTrack?.durationSec || 0,
        error: currentTrack ? null : 'Nie udało się załadować kolejki Audius.',
      });

      if (currentTrack) {
        this.setAudioSource(currentTrack);
      }
    } catch (error) {
      this.patchState({
        hasInitializedQueue: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Nie udało się połączyć z Audius.',
      });
    }
  }

  async play(): Promise<void> {
    await this.ensureQueueInitialized();

    if (!this.state.currentTrack) {
      this.patchState({ error: 'Brak utworu do odtworzenia.' });
      return;
    }

    if (this.state.volume <= 0) {
      this.autoResumeWhenVolumeRestored = true;
      return;
    }

    const activeTrack = this.state.currentTrack;
    const targetToken = ++this.playbackToken;
    this.manualPause = false;
    this.patchState({ isLoading: true, error: null });

    if (this.audio.dataset.trackId !== activeTrack.id) {
      this.setAudioSource(activeTrack);
    }

    try {
      await this.audio.play();
      if (targetToken !== this.playbackToken) {
        return;
      }
      this.patchState({
        isLoading: false,
        isPlaying: true,
        duration: this.audio.duration || activeTrack.durationSec || this.state.duration,
      });
    } catch (error) {
      if (targetToken !== this.playbackToken) {
        return;
      }
      if (this.isBenignAbortError(error)) {
        this.patchState({
          isLoading: false,
        });
        return;
      }
      this.patchState({
        isLoading: false,
        isPlaying: false,
        error: error instanceof Error ? error.message : 'Przeglądarka zablokowała odtwarzanie.',
      });
    }
  }

  pause(manual = true): void {
    this.manualPause = manual;
    this.autoResumeWhenVolumeRestored = !manual && this.state.volume <= 0;
    this.audio.pause();
    this.patchState({ isPlaying: false, isLoading: false });
  }

  async togglePlayback(): Promise<void> {
    if (this.state.isPlaying) {
      this.pause(true);
      return;
    }
    await this.play();
  }

  async playAtIndex(index: number): Promise<void> {
    const track = this.state.queue[index];
    if (!track) return;

    this.patchState({
      currentTrack: track,
      currentIndex: index,
      currentTime: 0,
      duration: track.durationSec || 0,
      error: null,
    });
    this.setAudioSource(track);
    await this.play();
  }

  async next(): Promise<void> {
    if (this.state.queue.length === 0) return;
    if (this.state.currentIndex >= this.state.queue.length - 1) {
      const freshQueue = await this.fetchFreshQueue(
        this.state.queue.map((track) => track.id),
        this.state.selectedMood,
      );
      if (freshQueue.length > 0) {
        this.patchState({
          queue: freshQueue,
          currentTrack: freshQueue[0],
          currentIndex: 0,
          currentTime: 0,
          duration: freshQueue[0].durationSec || 0,
          error: null,
        });
        this.setAudioSource(freshQueue[0]);
        await this.play();
        return;
      }
    }

    const nextIndex = this.state.currentIndex >= 0
      ? this.state.currentIndex + 1
      : 0;
    await this.playAtIndex(nextIndex);
  }

  async previous(): Promise<void> {
    if (this.state.queue.length === 0) return;
    if (this.audio.currentTime > 5 && this.state.currentTrack) {
      this.seek(0);
      return;
    }

    const previousIndex = this.state.currentIndex > 0
      ? this.state.currentIndex - 1
      : this.state.queue.length - 1;
    await this.playAtIndex(previousIndex);
  }

  seek(timeSec: number): void {
    if (!Number.isFinite(timeSec) || !this.state.currentTrack) return;
    const safeDuration = this.audio.duration || this.state.duration || this.state.currentTrack.durationSec || 0;
    const nextTime = Math.max(0, Math.min(timeSec, safeDuration));
    this.audio.currentTime = nextTime;
    this.patchState({ currentTime: nextTime });
  }

  setVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(volume, 1));
    const currentSettings = loadAudioSettings();
    if (Math.abs(currentSettings.musicVolume - clamped) < 0.001) {
      this.applyVolume(clamped);
      this.patchState({ volume: clamped });
      return;
    }

    saveAudioSettings({
      ...currentSettings,
      musicVolume: clamped,
    });
  }

  async addTrackNextAndPlay(track: AudiusTrackSummary): Promise<void> {
    const queue = [...this.state.queue];
    const insertIndex = this.state.currentIndex >= 0 ? this.state.currentIndex + 1 : queue.length;
    queue.splice(insertIndex, 0, track);

    this.patchState({
      queue,
      currentTrack: track,
      currentIndex: insertIndex,
      currentTime: 0,
      duration: track.durationSec || 0,
      error: null,
    });

    this.setAudioSource(track);
    await this.play();
  }

  async search(query: string): Promise<void> {
    const trimmed = query.trim();
    this.searchAbortController?.abort();
    this.searchAbortController = null;
    this.patchState({ error: null });

    if (trimmed.length < 2) {
      this.patchState({ searchResults: [], isLoading: false });
      return;
    }

    const controller = new AbortController();
    this.searchAbortController = controller;
    this.patchState({ isLoading: true });

    try {
      const results = await audiusApiClient.searchTracks(trimmed, 10, controller.signal);
      if (this.searchAbortController !== controller) {
        return;
      }
      this.patchState({
        searchResults: results,
        isLoading: false,
      });
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      this.patchState({
        searchResults: [],
        isLoading: false,
        error: error instanceof Error ? error.message : 'Wyszukiwanie Audius nie powiodło się.',
      });
    } finally {
      if (this.searchAbortController === controller) {
        this.searchAbortController = null;
      }
    }
  }

  private bindAudioEvents(): void {
    this.audio.addEventListener('timeupdate', () => {
      this.patchState({
        currentTime: Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0,
      });
    });

    this.audio.addEventListener('loadedmetadata', () => {
      this.patchState({
        duration: Number.isFinite(this.audio.duration) ? this.audio.duration : this.state.duration,
      });
    });

    this.audio.addEventListener('play', () => {
      this.patchState({ isPlaying: true, isLoading: false });
    });

    this.audio.addEventListener('pause', () => {
      this.patchState({ isPlaying: false, isLoading: false });
    });

    this.audio.addEventListener('waiting', () => {
      this.patchState({ isLoading: true });
    });

    this.audio.addEventListener('ended', () => {
      void this.next();
    });

    this.audio.addEventListener('error', () => {
      const mediaErrorCode = this.audio.error?.code;
      if (mediaErrorCode === MediaError.MEDIA_ERR_ABORTED) {
        this.patchState({
          isLoading: false,
        });
        return;
      }
      this.patchState({
        isLoading: false,
        isPlaying: false,
        error: 'Nie udało się odtworzyć wybranego utworu.',
      });
    });
  }

  private setAudioSource(track: AudiusTrackSummary): void {
    const src = audiusApiClient.getStreamUrl(track.id);
    if (this.audio.src !== src) {
      this.audio.src = src;
    }
    this.audio.dataset.trackId = track.id;
    this.audio.load();
  }

  private async fetchFreshQueue(
    excludedTrackIds: string[] = [],
    moodId: AudiusMoodId = this.state.selectedMood,
  ): Promise<AudiusTrackSummary[]> {
    const exclude = new Set(excludedTrackIds);
    const preset = AUDIUS_MOOD_PRESETS.find((item) => item.id === moodId)
      || AUDIUS_MOOD_PRESETS.find((item) => item.id === DEFAULT_AUDIUS_MOOD)
      || AUDIUS_MOOD_PRESETS[0];

    const aggregated = new Map<string, AudiusTrackSummary>();
    for (const query of preset.queries) {
      const results = await audiusApiClient.searchTracks(query, 12);
      results.forEach((track) => {
        if (!exclude.has(track.id) && !aggregated.has(track.id)) {
          aggregated.set(track.id, track);
        }
      });
    }

    const moodPool = Array.from(aggregated.values());
    if (moodPool.length >= 10) {
      return shuffle(moodPool).slice(0, 10);
    }

    const trending = await audiusApiClient.getTrending(25);
    const fallbackPool = trending.filter((track) => !exclude.has(track.id) && !aggregated.has(track.id));
    return shuffle([...moodPool, ...fallbackPool]).slice(0, 10);
  }

  private handleExternalAudioSettings(settings: AudioSettings): void {
    this.patchState({ volume: settings.musicVolume });
    this.applyVolume(settings.musicVolume);

    if (settings.musicVolume <= 0) {
      if (!this.audio.paused) {
        this.pause(false);
      }
      this.autoResumeWhenVolumeRestored = true;
      return;
    }

    if (this.autoResumeWhenVolumeRestored && !this.manualPause && this.state.hasActivated && this.state.currentTrack) {
      this.autoResumeWhenVolumeRestored = false;
      void this.play();
    }
  }

  private applyVolume(volume: number): void {
    this.audio.volume = Math.max(0, Math.min(volume, 1));
  }

  private patchState(patch: Partial<AudiusPlayerState>): void {
    this.state = {
      ...this.state,
      ...patch,
    };
    this.emit();
  }

  private emit(): void {
    const snapshot = this.getState();
    this.subscribers.forEach((listener) => {
      listener(snapshot);
    });
  }
}

export const audiusPlayerService = new AudiusPlayerService();
